/**
 * Console data loader — one hook for every /admin/* read so loading / error /
 * retry behave identically across sections (staff tools must never fail
 * silently: an error state with a retry beats a stale screen).
 */
import { useCallback, useEffect, useState } from "react";
import { isApiError } from "../../lib/api.js";
import { rawApi } from "../safety/raw-api.js";

export interface Loaded<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useLoad<T>(path: string): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    rawApi<T>("GET", path)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((err) => {
        setError(isApiError(err) ? err.message : "Load failed");
        setLoading(false);
      });
  }, [path]);

  useEffect(reload, [reload]);
  return { data, loading, error, reload };
}
